// Cliente Supabase para torre-binario · schema binario_wms em ZYONCS.
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL =
  import.meta.env?.VITE_BINARIO_SUPABASE_URL ||
  'https://hbrvoairynwsliyxxgsg.supabase.co';

const SUPABASE_KEY =
  import.meta.env?.VITE_BINARIO_SUPABASE_KEY ||
  'sb_publishable_a0ZR31NQNuDyXgm8uqoQ0g_DW96AQLB';

export const db = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_binario_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  db: { schema: 'binario_wms' },
});

export const auth = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession: true,
    storageKey: 'zzy_binario_session',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});

// ─── Helpers de domínio ──────────────────────────────────────────────
// Binário Distribuidora: domínio oficial @binario.net (https://www.binario.net)
// Ícaro Express: domínio oficial @icaroexpress.com
const ALLOWED_DOMAINS = [/@icaroexpress\.com$/i, /@binario\.net$/i];

export function isAuthorizedEmail(email) {
  if (!email) return false;
  return ALLOWED_DOMAINS.some((re) => re.test(email));
}

export function emailDomain(email) {
  return (email || '').split('@')[1]?.toLowerCase() || '';
}

export function isBinarioUser(email) {
  return /@binario\.net$/i.test(email || '');
}

export async function getCurrentUser() {
  const { data } = await auth.auth.getUser();
  return data?.user || null;
}

export async function signInMagic(email, redirectTo) {
  if (!isAuthorizedEmail(email)) {
    throw new Error('Acesso restrito a @icaroexpress.com e @binario.*');
  }
  const { error } = await auth.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo || location.origin },
  });
  if (error) throw error;
}

export async function signOut() {
  await auth.auth.signOut();
  location.reload();
}

// ─── Repository ──────────────────────────────────────────────────────
export const repo = {
  async stats() {
    const [{ count: nfe }, { count: skus }, { count: ctes }, mov, vlrEstoque, divergencias] = await Promise.all([
      db.from('nfe_xml').select('*', { count: 'exact', head: true }),
      db.from('sku_master').select('*', { count: 'exact', head: true }),
      db.from('cte_frete').select('*', { count: 'exact', head: true }),
      db.from('movimento').select('fluxo, vlr_total'),
      db.from('vw_posicao_estoque').select('valor_estoque'),
      db.from('divergencia').select('*', { count: 'exact', head: true }).in('status', ['aberta', 'em_apuracao']),
    ]);
    let inboundVlr = 0, outboundVlr = 0;
    if (mov.data) {
      for (const m of mov.data) {
        const v = Number(m.vlr_total) || 0;
        if (m.fluxo === 'inbound') inboundVlr += v;
        else outboundVlr += v;
      }
    }
    const valorEstoque = (vlrEstoque.data || []).reduce((a, r) => a + (Number(r.valor_estoque) || 0), 0);
    return {
      nfes: nfe || 0,
      skus: skus || 0,
      ctes: ctes || 0,
      movimentos: mov.data?.length || 0,
      inbound_vlr: inboundVlr,
      outbound_vlr: outboundVlr,
      valor_estoque_atual: valorEstoque,
      divergencias_abertas: divergencias.count || 0,
    };
  },

  async health() {
    const { data, error } = await db.from('vw_health').select('*');
    if (error) throw error;
    return data || [];
  },

  async abcXyz() {
    const { data, error } = await db.from('vw_abc_xyz').select('*');
    if (error) throw error;
    return data || [];
  },

  async topClientes(limit = 12) {
    const { data, error } = await db.from('vw_top_clientes').select('*').limit(limit);
    if (error) throw error;
    return data || [];
  },

  async estoque({ classeAbc, limit = 300 } = {}) {
    let q = db.from('vw_posicao_estoque')
      .select('sku_codigo,descricao,categoria,classe_abc,classe_xyz,endereco_atual,saldo_qtd,valor_referencia,valor_estoque,ultima_entrada,ultima_saida')
      .order('valor_estoque', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (classeAbc) q = q.eq('classe_abc', classeAbc);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  async movimentos({ fluxo, limit = 80 } = {}) {
    let q = db.from('movimento')
      .select('id,data_emissao,nf_numero:chave_nfe,fluxo,natureza,sku_codigo,descricao,qtd,vlr_unit,vlr_total,cfop,endereco,conferente,divergencia')
      .order('data_emissao', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (fluxo) q = q.eq('fluxo', fluxo);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  async fretes({ direcao, limit = 100 } = {}) {
    let q = db.from('cte_frete')
      .select('cte_numero,nf_numero,data_frete,destinatario,cidade_destino,valor_nf,direcao_frete,match_status,matched_chave_nfe')
      .order('data_frete', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (direcao) q = q.eq('direcao_frete', direcao);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  // ─── Upload XML via edge function ─────────────────────────────────────
  async ingestXml(xmlText, source = 'xml_nfeProc') {
    const { data: { session } } = await auth.auth.getSession();
    const token = session?.access_token || SUPABASE_KEY;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/binario-wms-ingest`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/xml',
        'X-Ingest-Source': source,
        'apikey': SUPABASE_KEY,
      },
      body: xmlText,
    });
    const json = await res.json();
    if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  },

  // ─── Volumes ──────────────────────────────────────────────────────────
  async volumesAtivos({ limit = 300, status, sku } = {}) {
    let q = db.from('vw_volume_ativo').select('*').limit(limit);
    if (status) q = q.eq('status', status);
    if (sku) q = q.eq('sku_codigo', sku);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },
  async criarVolume(payload) {
    const codigo = generateVolumeCode();
    const row = { volume_codigo: codigo, ...payload, qr_payload: `BINW:VOL:${codigo}` };
    const { data, error } = await db.from('volume').insert(row).select().single();
    if (error) throw error;
    await this.logEtiqueta({ tipo: 'volume', ref_codigo: codigo, qr_payload: row.qr_payload, barcode_payload: codigo });
    return data;
  },
  async atualizarVolume(volume_codigo, patch) {
    const { data, error } = await db.from('volume').update(patch).eq('volume_codigo', volume_codigo).select().single();
    if (error) throw error;
    return data;
  },
  async getVolume(volume_codigo) {
    const { data, error } = await db.from('vw_volume_ativo').select('*').eq('volume_codigo', volume_codigo).maybeSingle();
    if (error) throw error;
    return data;
  },

  // ─── Endereços / Ocupação ─────────────────────────────────────────────
  async ocupacaoEnderecos({ limit = 500 } = {}) {
    const { data, error } = await db.from('vw_ocupacao_endereco').select('*').limit(limit);
    if (error) throw error;
    return data || [];
  },
  async listEnderecos() {
    const { data, error } = await db.from('endereco').select('endereco_codigo,rua,modulo,nivel,posicao,tipo,golden_zone,capacidade_qtd,ativo')
      .eq('ativo', true).order('rua').order('modulo').order('nivel');
    if (error) throw error;
    return data || [];
  },
  async criarEndereco(payload) {
    const { data, error } = await db.from('endereco').insert(payload).select().single();
    if (error) throw error;
    await this.logEtiqueta({ tipo: 'endereco', ref_codigo: data.endereco_codigo, qr_payload: `BINW:LOC:${data.endereco_codigo}`, barcode_payload: data.endereco_codigo });
    return data;
  },

  async realocarVolume({ volume_codigo, endereco_destino, motivo = 'realocacao', responsavel = '' }) {
    // 1. busca origem
    const vol = await this.getVolume(volume_codigo);
    if (!vol) throw new Error(`Volume ${volume_codigo} não encontrado`);
    const endereco_origem = vol.endereco;
    // 2. atualiza
    await db.from('volume').update({
      endereco: endereco_destino,
      status: endereco_destino ? 'endereçado' : 'recebido',
      endereçado_em: endereco_destino ? new Date().toISOString() : null,
    }).eq('volume_codigo', volume_codigo);
    // 3. log
    await db.from('realocacao_log').insert({
      volume_codigo, sku_codigo: vol.sku_codigo, qtd: vol.qtd,
      endereco_origem, endereco_destino, motivo, responsavel,
    });
    return { ok: true, from: endereco_origem, to: endereco_destino };
  },

  async logEtiqueta({ tipo, ref_codigo, qr_payload, barcode_payload, quantidade = 1, impresso_por = '' }) {
    return await db.from('etiqueta_impressa').insert({ tipo, ref_codigo, qr_payload, barcode_payload, quantidade, impresso_por });
  },

  // ─── Picking ───────────────────────────────────────────────────────────
  async pickingPendentes({ limit = 50 } = {}) {
    const { data, error } = await db.from('vw_picking_pendente').select('*').limit(limit);
    if (error) throw error;
    return data || [];
  },
  async pickingItens(picking_list_id) {
    const { data, error } = await db.from('picking_item').select('*').eq('picking_list_id', picking_list_id).order('ordem');
    if (error) throw error;
    return data || [];
  },
  async criarPicking({ destino_chave_nfe, pedido_cliente, destinatario, cidade_destino, prioridade = 5, itens = [] }) {
    const { data: pl, error: e1 } = await db.from('picking_list').insert({
      destino_chave_nfe, pedido_cliente, destinatario, cidade_destino, prioridade, status: 'planejado',
    }).select().single();
    if (e1) throw e1;
    if (itens.length > 0) {
      const rows = itens.map((it, i) => ({
        picking_list_id: pl.id, ordem: it.ordem ?? i + 1,
        sku_codigo: it.sku_codigo, descricao: it.descricao,
        qtd_planejada: it.qtd, endereco_alvo: it.endereco_alvo || null,
      }));
      const { error: e2 } = await db.from('picking_item').insert(rows);
      if (e2) throw e2;
    }
    return pl;
  },
  async atualizarPicking(id, patch) {
    const { data, error } = await db.from('picking_list').update(patch).eq('id', id).select().single();
    if (error) throw error;
    return data;
  },
  async confirmarItemPicking(picking_item_id, { qtd_separada, volume_codigo, divergencia }) {
    const patch = { qtd_separada, volume_codigo: volume_codigo || null, separado_em: new Date().toISOString() };
    if (divergencia) patch.divergencia = divergencia;
    const { data, error } = await db.from('picking_item').update(patch).eq('id', picking_item_id).select().single();
    if (error) throw error;
    return data;
  },

  // ─── Remessas sem nota ────────────────────────────────────────────────
  async remessasSemNota({ status, limit = 80 } = {}) {
    let q = db.from('remessa_sem_nota').select('*').order('data_evento', { ascending: false }).limit(limit);
    if (status) q = q.eq('status', status);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },
  async criarRemessaSemNota({ tipo, fluxo, parceiro_nome, parceiro_cnpj, responsavel, conferente, motivo, numero_externo, valor_estimado, observacoes, itens = [] }) {
    const { data: r, error: e1 } = await db.from('remessa_sem_nota').insert({
      tipo, fluxo, parceiro_nome, parceiro_cnpj, responsavel, conferente, motivo, numero_externo, valor_estimado, observacoes,
      qtd_volumes: itens.length, status: 'aberta',
    }).select().single();
    if (e1) throw e1;
    if (itens.length > 0) {
      const rows = itens.map(it => ({
        remessa_id: r.id, sku_codigo: it.sku_codigo, descricao: it.descricao,
        qtd: it.qtd || 1, unid: it.unid, volume_codigo: it.volume_codigo,
        endereco_origem: it.endereco_origem, endereco_destino: it.endereco_destino,
        serial: it.serial, observacoes: it.observacoes,
      }));
      await db.from('remessa_sem_nota_item').insert(rows);
    }
    return r;
  },
};

// helper: gera código volume único e curto VOL-YYYYMMDD-XXXXXX
function generateVolumeCode() {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `VOL-${ymd}-${rand}`;
}
export { generateVolumeCode };
